import React, { useState, useEffect, useRef } from 'react';
import { useLocale } from '@/contexts/LocaleContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  TrendingUp, Loader2, Sparkles, Palette, Shirt, 
  FileText, Globe, Calendar, Users, Image, Download, 
  RefreshCw, Wand2, BookOpen, X, History,
  Printer, Edit3, Droplets, Eye, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fetchWithAuth = async (url, options = {}) => {
  const token = localStorage.getItem('texhub_token') || sessionStorage.getItem('texhub_token');
  return fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      'Authorization': token ? `Bearer ${token}` : '',
    }
  });
};

const CATEGORIES = [
  { id: 'dama', name: 'Dama', nameEn: 'Women', icon: '👩' },
  { id: 'caballero', name: 'Caballero', nameEn: 'Men', icon: '👨' },
  { id: 'infantil', name: 'Infantil', nameEn: 'Kids', icon: '👶' },
  { id: 'deportivo', name: 'Deportivo', nameEn: 'Sportswear', icon: '🏃' },
  { id: 'uniformes', name: 'Uniformes', nameEn: 'Uniforms', icon: '👔' },
  { id: 'home_textiles', name: 'Home Textiles', nameEn: 'Home Textiles', icon: '🏠' },
];

const MARKETS = [
  { id: 'mexico', name: 'México', nameEn: 'Mexico', flag: '🇲🇽' },
  { id: 'usa', name: 'Estados Unidos', nameEn: 'United States', flag: '🇺🇸' },
  { id: 'latam', name: 'Latinoamérica', nameEn: 'Latin America', flag: '🌎' },
  { id: 'global', name: 'Global', nameEn: 'Global', flag: '🌍' },
];

const SEASONS = [
  { id: 'ss', name: 'Primavera-Verano', nameEn: 'Spring-Summer', icon: '☀️' },
  { id: 'fw', name: 'Otoño-Invierno', nameEn: 'Fall-Winter', icon: '❄️' },
];

const YEARS = ['2025', '2026', '2027', '2028'];

const COLOR_VARIATIONS = [
  { id: 'color', name: 'Nueva Paleta', nameEn: 'New Palette', icon: '🎨' },
  { id: 'warm', name: 'Tonos Cálidos', nameEn: 'Warm Tones', icon: '🔥' },
  { id: 'cool', name: 'Tonos Fríos', nameEn: 'Cool Tones', icon: '❄️' },
  { id: 'neutral', name: 'Neutros', nameEn: 'Neutrals', icon: '🤍' },
  { id: 'pastel', name: 'Pasteles', nameEn: 'Pastels', icon: '🌸' },
  { id: 'vibrant', name: 'Vibrantes', nameEn: 'Vibrant', icon: '💥' },
];

const TexTrendAI = ({ onCreditError, onSendToTexChat }) => {
  const { isUSA } = useLocale();
  const printRef = useRef(null);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('analyze');
  
  // Form state
  const [category, setCategory] = useState('');
  const [market, setMarket] = useState('');
  const [season, setSeason] = useState('');
  const [year, setYear] = useState('');
  const [customQuestion, setCustomQuestion] = useState('');
  
  // Response state
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [generatingMoodboard, setGeneratingMoodboard] = useState(false);
  const [moodboard, setMoodboard] = useState(null);
  
  // Pattern state
  const [generatingPattern, setGeneratingPattern] = useState(null);
  const [generatedPatterns, setGeneratedPatterns] = useState({});
  const [editingPattern, setEditingPattern] = useState(null);
  const [editPrompt, setEditPrompt] = useState('');
  const [generatingVariation, setGeneratingVariation] = useState(null);
  
  // Modal state
  const [imageModal, setImageModal] = useState(null); // {src, title, description}
  
  // History state
  const [historyAnalyses, setHistoryAnalyses] = useState([]);
  const [historyPatterns, setHistoryPatterns] = useState([]);
  const [historyMoodboards, setHistoryMoodboards] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const isFormComplete = category && market && season && year;

  // Fetch history when tab changes
  useEffect(() => {
    if (activeTab === 'history') {
      fetchHistory();
    }
  }, [activeTab]);

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const [analysesRes, patternsRes, moodboardsRes] = await Promise.all([
        fetchWithAuth(`${API_URL}/api/textrend/history/analyses?limit=10`),
        fetchWithAuth(`${API_URL}/api/textrend/history/patterns?limit=20`),
        fetchWithAuth(`${API_URL}/api/textrend/history/moodboards?limit=10`)
      ]);
      
      if (analysesRes.ok) setHistoryAnalyses(await analysesRes.json());
      if (patternsRes.ok) setHistoryPatterns(await patternsRes.json());
      if (moodboardsRes.ok) setHistoryMoodboards(await moodboardsRes.json());
    } catch (e) {
      console.error('Error fetching history:', e);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleAnalyze = async () => {
    if (!isFormComplete) {
      toast.error(isUSA ? 'Please complete all fields' : 'Por favor completa todos los campos');
      return;
    }

    setLoading(true);
    setResult(null);
    setMoodboard(null);
    setGeneratedPatterns({});

    try {
      const res = await fetchWithAuth(`${API_URL}/api/textrend/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category, market, season, year, custom_question: customQuestion || null })
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Error al analizar');
      }

      const data = await res.json();
      setResult(data);
      toast.success(isUSA ? 'Analysis complete!' : '¡Análisis completado!');
    } catch (e) {
      if (onCreditError && onCreditError(e.message)) {
        // Error was handled by parent
      } else {
        toast.error(e.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateMoodboard = async () => {
    if (!result) return;
    setGeneratingMoodboard(true);

    try {
      const res = await fetchWithAuth(`${API_URL}/api/textrend/moodboard`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category, market, season, year, trend_summary: result.executive_summary })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error');
      setMoodboard(data);
      toast.success(isUSA ? 'Moodboard generated!' : '¡Moodboard generado!');
    } catch (e) {
      if (onCreditError && onCreditError(e.message)) {
        // Error was handled by parent
      } else {
        toast.error(e.message);
      }
    } finally {
      setGeneratingMoodboard(false);
    }
  };

  const handleGeneratePattern = async (trendIndex, trend) => {
    setGeneratingPattern(trendIndex);
    
    try {
      const res = await fetchWithAuth(`${API_URL}/api/textrend/generate-pattern`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pattern_name: trend.name,
          pattern_description: trend.description,
          category, season, year,
          color_palette: result.color_palette?.slice(0, 4) || []
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error');
      setGeneratedPatterns(prev => ({ ...prev, [trendIndex]: data }));
      toast.success(isUSA ? 'Pattern generated!' : '¡Estampado generado!');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setGeneratingPattern(null);
    }
  };

  const handleEditPattern = async (patternData) => {
    if (!editPrompt.trim()) {
      toast.error(isUSA ? 'Please enter edit instructions' : 'Ingresa las instrucciones de edición');
      return;
    }
    
    setEditingPattern({ ...patternData, loading: true });
    
    try {
      const res = await fetchWithAuth(`${API_URL}/api/textrend/edit-pattern`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pattern_id: patternData.pattern_id,
          edit_prompt: editPrompt
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error');
      
      // Update the pattern in state
      setEditingPattern({ ...data, original: patternData });
      setEditPrompt('');
      toast.success(isUSA ? 'Pattern edited!' : '¡Estampado editado!');
    } catch (e) {
      toast.error(e.message);
      setEditingPattern(patternData);
    }
  };

  const handleGenerateVariation = async (patternData, variationType) => {
    setGeneratingVariation(variationType);
    
    try {
      const res = await fetchWithAuth(`${API_URL}/api/textrend/pattern-variations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pattern_id: patternData.pattern_id,
          variation_type: variationType
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error');
      
      // Show variation in modal
      setImageModal({
        src: `data:image/jpeg;base64,${data.image_base64}`,
        title: data.pattern_name,
        description: data.variation_type,
        patternData: data
      });
      toast.success(isUSA ? 'Variation generated!' : '¡Variación generada!');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setGeneratingVariation(null);
    }
  };

  const handleUseInDesigner = (patternData) => {
    sessionStorage.setItem('textrend_pattern', JSON.stringify({
      image_base64: patternData.image_base64,
      pattern_name: patternData.pattern_name,
      description: patternData.description,
      from_textrend: true
    }));
    toast.success(isUSA ? 'Pattern ready! Opening Fashion Designer...' : '¡Estampado listo! Abriendo Diseñador de Modas...');
    window.dispatchEvent(new CustomEvent('switch-to-designer', { detail: { hasPattern: true } }));
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>TEXTREND AI - ${getCategoryName(category)} ${getSeasonName(season)} ${year}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; max-width: 900px; margin: 0 auto; }
            h1 { color: #4F46E5; border-bottom: 2px solid #4F46E5; padding-bottom: 10px; }
            h2 { color: #374151; margin-top: 30px; }
            .color-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin: 20px 0; }
            .color-item { text-align: center; }
            .color-swatch { width: 80px; height: 80px; border-radius: 8px; margin: 0 auto 8px; border: 1px solid #ddd; }
            .color-name { font-weight: bold; font-size: 12px; }
            .color-hex { font-family: monospace; font-size: 11px; color: #666; }
            .section { margin: 25px 0; padding: 15px; background: #f9fafb; border-radius: 8px; }
            .trend-item { margin: 15px 0; padding: 12px; background: white; border-radius: 6px; }
            .methodology-item { font-size: 12px; color: #666; margin: 5px 0; }
            .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; font-size: 11px; color: #888; }
            .badge { display: inline-block; padding: 4px 12px; background: #E0E7FF; color: #4F46E5; border-radius: 20px; font-size: 12px; margin-right: 8px; }
            @media print { body { padding: 20px; } }
          </style>
        </head>
        <body>
          <h1>TEXTREND AI - Análisis de Tendencias</h1>
          <div>
            <span class="badge">${CATEGORIES.find(c => c.id === category)?.icon} ${getCategoryName(category)}</span>
            <span class="badge">${MARKETS.find(m => m.id === market)?.flag} ${getMarketName(market)}</span>
            <span class="badge">${SEASONS.find(s => s.id === season)?.icon} ${getSeasonName(season)} ${year}</span>
          </div>
          
          <h2>Resumen Ejecutivo</h2>
          <div class="section">${result?.executive_summary?.replace(/\n/g, '<br>')}</div>
          
          <h2>Paleta de Colores</h2>
          <div class="color-grid">
            ${result?.color_palette?.map(c => `
              <div class="color-item">
                <div class="color-swatch" style="background-color: ${c.hex}"></div>
                <div class="color-name">${c.name}</div>
                <div class="color-hex">${c.hex}</div>
              </div>
            `).join('') || ''}
          </div>
          
          <h2>Tendencias de Estampados</h2>
          ${result?.print_trends?.map(t => `
            <div class="trend-item">
              <strong>${t.name}</strong>
              <p>${t.description}</p>
              <small>Aplicaciones: ${t.applications}</small>
            </div>
          `).join('') || ''}
          
          <h2>Materiales Recomendados</h2>
          ${result?.materials?.map(m => `
            <div class="trend-item">
              <strong>${m.name}</strong>
              <p>${m.description}</p>
              <small>Acabados: ${m.finishes}</small>
            </div>
          `).join('') || ''}
          
          <h2>Aplicación para México</h2>
          <div class="section">${result?.mexico_application?.replace(/\n/g, '<br>')}</div>
          
          <h2>Metodología</h2>
          <div class="methodology-item">• Tendencias globales de moda</div>
          <div class="methodology-item">• Ferias textiles internacionales</div>
          <div class="methodology-item">• Análisis de pasarelas</div>
          <div class="methodology-item">• Comportamiento del consumidor</div>
          <div class="methodology-item">• Inteligencia artificial aplicada a moda y textil</div>
          <div class="methodology-item">• Información pública de la industria</div>
          
          <div class="footer">
            Generado por TEXTREND AI - TexHub.com.mx<br>
            Fecha: ${new Date().toLocaleDateString('es-MX')}
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const loadHistoryAnalysis = async (analysis) => {
    if (analysis.result) {
      setResult(analysis.result);
      setCategory(analysis.category);
      setMarket(analysis.market);
      setSeason(analysis.season);
      setYear(analysis.year);
      setActiveTab('analyze');
      toast.success(isUSA ? 'Analysis loaded' : 'Análisis cargado');
    } else {
      // Fetch full analysis
      try {
        const res = await fetchWithAuth(`${API_URL}/api/textrend/analysis/${analysis.analysis_id}`);
        if (res.ok) {
          const data = await res.json();
          setResult(data.result);
          setCategory(data.category);
          setMarket(data.market);
          setSeason(data.season);
          setYear(data.year);
          setActiveTab('analyze');
        }
      } catch (e) {
        toast.error('Error loading analysis');
      }
    }
  };

  const resetForm = () => {
    setCategory('');
    setMarket('');
    setSeason('');
    setYear('');
    setCustomQuestion('');
    setResult(null);
    setMoodboard(null);
    setGeneratedPatterns({});
    setEditingPattern(null);
  };

  const getCategoryName = (id) => CATEGORIES.find(c => c.id === id)?.[isUSA ? 'nameEn' : 'name'] || id;
  const getMarketName = (id) => MARKETS.find(m => m.id === id)?.[isUSA ? 'nameEn' : 'name'] || id;
  const getSeasonName = (id) => SEASONS.find(s => s.id === id)?.[isUSA ? 'nameEn' : 'name'] || id;

  return (
    <div className="space-y-6" data-testid="textrend-ai">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-indigo-600" />
            TEXTREND AI
          </h2>
          <p className="text-slate-500 text-sm mt-1">
            {isUSA ? 'AI Fashion & Textile Trend Analyst' : 'Analista de Tendencias de Moda y Textil con IA'}
          </p>
        </div>
        <div className="flex gap-2">
          {result && (
            <>
              <Button variant="outline" size="sm" onClick={handlePrint}>
                <Printer className="w-4 h-4 mr-1" />
                {isUSA ? 'Print' : 'Imprimir'}
              </Button>
              <Button variant="outline" size="sm" onClick={resetForm}>
                <RefreshCw className="w-4 h-4 mr-1" />
                {isUSA ? 'New' : 'Nuevo'}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="analyze">
            <Sparkles className="w-4 h-4 mr-1" />
            {isUSA ? 'Analyze' : 'Analizar'}
          </TabsTrigger>
          <TabsTrigger value="history">
            <History className="w-4 h-4 mr-1" />
            {isUSA ? 'History' : 'Historial'}
          </TabsTrigger>
        </TabsList>

        {/* ANALYZE TAB */}
        <TabsContent value="analyze" className="space-y-6">
          {/* Form Section */}
          {!result && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  {isUSA ? 'Configure Your Analysis' : 'Configura tu Análisis'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Category */}
                <div>
                  <Label className="flex items-center gap-2 mb-3">
                    <Users className="w-4 h-4" />
                    {isUSA ? 'Category' : 'Categoría'}
                  </Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {CATEGORIES.map(cat => (
                      <button
                        key={cat.id}
                        onClick={() => setCategory(cat.id)}
                        className={`p-3 rounded-lg border text-left transition-all ${
                          category === cat.id 
                            ? 'border-indigo-500 bg-indigo-50 text-indigo-700' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <span className="text-xl mr-2">{cat.icon}</span>
                        <span className="text-sm font-medium">{isUSA ? cat.nameEn : cat.name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Market */}
                <div>
                  <Label className="flex items-center gap-2 mb-3">
                    <Globe className="w-4 h-4" />
                    {isUSA ? 'Market' : 'Mercado'}
                  </Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {MARKETS.map(mkt => (
                      <button
                        key={mkt.id}
                        onClick={() => setMarket(mkt.id)}
                        className={`p-3 rounded-lg border text-center transition-all ${
                          market === mkt.id 
                            ? 'border-indigo-500 bg-indigo-50 text-indigo-700' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <span className="text-xl block mb-1">{mkt.flag}</span>
                        <span className="text-sm font-medium">{isUSA ? mkt.nameEn : mkt.name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Season & Year */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-2 mb-3">
                      <Calendar className="w-4 h-4" />
                      {isUSA ? 'Season' : 'Temporada'}
                    </Label>
                    <div className="grid grid-cols-2 gap-2">
                      {SEASONS.map(s => (
                        <button
                          key={s.id}
                          onClick={() => setSeason(s.id)}
                          className={`p-3 rounded-lg border text-center transition-all ${
                            season === s.id 
                              ? 'border-indigo-500 bg-indigo-50 text-indigo-700' 
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <span className="text-xl block mb-1">{s.icon}</span>
                          <span className="text-xs font-medium">{isUSA ? s.nameEn : s.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="flex items-center gap-2 mb-3">
                      <Calendar className="w-4 h-4" />
                      {isUSA ? 'Year' : 'Año'}
                    </Label>
                    <Select value={year} onValueChange={setYear}>
                      <SelectTrigger>
                        <SelectValue placeholder={isUSA ? 'Select year' : 'Seleccionar año'} />
                      </SelectTrigger>
                      <SelectContent>
                        {YEARS.map(y => (
                          <SelectItem key={y} value={y}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Custom Question */}
                <div>
                  <Label className="mb-2 block">{isUSA ? 'Additional Question (Optional)' : 'Pregunta Adicional (Opcional)'}</Label>
                  <Textarea
                    value={customQuestion}
                    onChange={(e) => setCustomQuestion(e.target.value)}
                    placeholder={isUSA ? 'E.g.: What prints will be trending?' : 'Ej: ¿Qué estampados serán tendencia?'}
                    rows={2}
                  />
                </div>

                {/* Analyze Button */}
                <Button 
                  className="w-full bg-indigo-600 hover:bg-indigo-700 py-6 text-lg"
                  onClick={handleAnalyze}
                  disabled={loading || !isFormComplete}
                >
                  {loading ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> {isUSA ? 'Analyzing...' : 'Analizando...'}</>
                  ) : (
                    <><Sparkles className="w-5 h-5 mr-2" /> {isUSA ? 'Analyze Trends' : 'Analizar Tendencias'}</>
                  )}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Results Section */}
          {result && (
            <div className="space-y-6" ref={printRef}>
              {/* Summary Badge */}
              <div className="flex flex-wrap gap-2 items-center justify-between">
                <div className="flex flex-wrap gap-2 items-center">
                  <Badge variant="outline" className="text-sm py-1 px-3">
                    {CATEGORIES.find(c => c.id === category)?.icon} {getCategoryName(category)}
                  </Badge>
                  <Badge variant="outline" className="text-sm py-1 px-3">
                    {MARKETS.find(m => m.id === market)?.flag} {getMarketName(market)}
                  </Badge>
                  <Badge variant="outline" className="text-sm py-1 px-3">
                    {SEASONS.find(s => s.id === season)?.icon} {getSeasonName(season)} {year}
                  </Badge>
                </div>
                {onSendToTexChat && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                    onClick={() => {
                      const contextData = {
                        category: getCategoryName(category),
                        market: getMarketName(market),
                        season: `${getSeasonName(season)} ${year}`
                      };
                      onSendToTexChat(contextData);
                    }}
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    {isUSA ? 'Ask TexChat AI' : 'Preguntar a TexChat AI'}
                  </Button>
                )}
              </div>

              {/* Executive Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-indigo-700">
                    <FileText className="w-5 h-5" />
                    {isUSA ? 'Executive Summary' : 'Resumen Ejecutivo'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-700 leading-relaxed whitespace-pre-line">{result.executive_summary}</p>
                </CardContent>
              </Card>

              {/* Color Palette */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-indigo-700">
                    <Palette className="w-5 h-5" />
                    {isUSA ? 'Color Palette' : 'Paleta de Colores'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {result.color_palette?.map((color, idx) => (
                      <div key={idx} className="rounded-lg overflow-hidden border border-slate-200 hover:shadow-md transition-shadow">
                        <div className="h-20 w-full cursor-pointer" style={{ backgroundColor: color.hex }}
                          onClick={() => setImageModal({ 
                            type: 'color',
                            color: color,
                            title: color.name
                          })}
                        />
                        <div className="p-3 bg-white">
                          <p className="font-semibold text-sm">{color.name}</p>
                          <div className="flex items-center gap-2">
                            <p className="text-xs text-slate-500 font-mono">{color.hex}</p>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(color.hex);
                                toast.success(isUSA ? 'HEX code copied!' : '¡Código HEX copiado!');
                              }}
                              className="text-xs text-slate-400 hover:text-slate-600"
                              title={isUSA ? 'Copy HEX' : 'Copiar HEX'}
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </button>
                          </div>
                          <a 
                            href="https://www.pantone.com/color-finder"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-indigo-600 hover:text-indigo-800 hover:underline mt-1 inline-flex items-center gap-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(color.hex);
                              toast.success(isUSA ? 'HEX copied! Paste it in the Pantone finder' : '¡HEX copiado! Pégalo en el buscador de Pantone');
                            }}
                          >
                            {isUSA ? 'Find Pantone equivalent' : 'Buscar equivalencia Pantone'}
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </a>
                          <p className="text-xs text-slate-600 mt-1 line-clamp-2">{color.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Print Trends with Generation */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-indigo-700">
                    <Image className="w-5 h-5" />
                    {isUSA ? 'Print & Pattern Trends' : 'Tendencias de Estampados'}
                  </CardTitle>
                  <CardDescription>
                    {isUSA 
                      ? 'Generate visual samples and create color variations'
                      : 'Genera muestras visuales y crea variaciones de color'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {result.print_trends?.map((trend, idx) => (
                      <div key={idx} className="p-4 bg-slate-50 rounded-lg">
                        <div className="flex flex-col md:flex-row gap-4">
                          {/* Pattern Info */}
                          <div className="flex-1">
                            <h4 className="font-semibold text-slate-800">{trend.name}</h4>
                            <p className="text-sm text-slate-600 mt-1">{trend.description}</p>
                            {trend.applications && (
                              <p className="text-xs text-indigo-600 mt-2">
                                <span className="font-medium">{isUSA ? 'Applications:' : 'Aplicaciones:'}</span> {trend.applications}
                              </p>
                            )}
                            
                            {/* Action Buttons */}
                            <div className="mt-3 flex flex-wrap gap-2">
                              <Button
                                size="sm"
                                variant={generatedPatterns[idx] ? "outline" : "default"}
                                className={!generatedPatterns[idx] ? "bg-purple-600 hover:bg-purple-700" : ""}
                                onClick={() => handleGeneratePattern(idx, trend)}
                                disabled={generatingPattern === idx}
                              >
                                {generatingPattern === idx ? (
                                  <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> {isUSA ? 'Generating...' : 'Generando...'}</>
                                ) : generatedPatterns[idx] ? (
                                  <><RefreshCw className="w-4 h-4 mr-1" /> {isUSA ? 'Regenerate' : 'Regenerar'}</>
                                ) : (
                                  <><Wand2 className="w-4 h-4 mr-1" /> {isUSA ? 'Generate' : 'Generar'}</>
                                )}
                              </Button>
                              
                              {generatedPatterns[idx] && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                                    onClick={() => handleUseInDesigner(generatedPatterns[idx])}
                                  >
                                    <Shirt className="w-4 h-4 mr-1" />
                                    {isUSA ? 'Use in Designer' : 'Usar en Diseñador'}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setEditingPattern(generatedPatterns[idx])}
                                  >
                                    <Edit3 className="w-4 h-4 mr-1" />
                                    {isUSA ? 'Edit' : 'Editar'}
                                  </Button>
                                  <Select onValueChange={(v) => handleGenerateVariation(generatedPatterns[idx], v)}>
                                    <SelectTrigger className="w-auto h-8">
                                      <Droplets className="w-4 h-4 mr-1" />
                                      <span className="text-xs">{isUSA ? 'Variations' : 'Variaciones'}</span>
                                    </SelectTrigger>
                                    <SelectContent>
                                      {COLOR_VARIATIONS.map(v => (
                                        <SelectItem key={v.id} value={v.id}>
                                          {v.icon} {isUSA ? v.nameEn : v.name}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </>
                              )}
                            </div>
                          </div>
                          
                          {/* Generated Pattern Preview */}
                          {generatedPatterns[idx] && (
                            <div 
                              className="w-full md:w-48 h-48 rounded-lg overflow-hidden border-2 border-purple-200 cursor-pointer hover:border-purple-400 transition-colors"
                              onClick={() => setImageModal({
                                src: `data:image/jpeg;base64,${generatedPatterns[idx].image_base64}`,
                                title: generatedPatterns[idx].pattern_name,
                                description: generatedPatterns[idx].description,
                                patternData: generatedPatterns[idx]
                              })}
                            >
                              <img 
                                src={`data:image/jpeg;base64,${generatedPatterns[idx].image_base64}`}
                                alt={trend.name}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Materials */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-indigo-700">
                    <Shirt className="w-5 h-5" />
                    {isUSA ? 'Recommended Materials' : 'Materiales Recomendados'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {result.materials?.map((mat, idx) => (
                      <div key={idx} className="p-4 border border-slate-200 rounded-lg">
                        <h4 className="font-semibold text-slate-800">{mat.name}</h4>
                        <p className="text-sm text-slate-600 mt-1">{mat.description}</p>
                        {mat.finishes && (
                          <p className="text-xs text-slate-500 mt-2">
                            <span className="font-medium">{isUSA ? 'Finishes:' : 'Acabados:'}</span> {mat.finishes}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Mexico Application */}
              {result.mexico_application && (
                <Card className="border-emerald-200 bg-emerald-50/50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-emerald-700">
                      🇲🇽 {isUSA ? 'Application for Mexico' : 'Aplicación para México'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-700 leading-relaxed whitespace-pre-line">{result.mexico_application}</p>
                  </CardContent>
                </Card>
              )}

              {/* Moodboard Button & Display */}
              <Card className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                    <div>
                      <h3 className="text-xl font-bold">{isUSA ? 'Generate Visual Moodboard' : 'Generar Moodboard Visual'}</h3>
                      <p className="text-indigo-100 text-sm mt-1">
                        {isUSA 
                          ? 'Create a professional moodboard with inspirational images'
                          : 'Crea un moodboard profesional con imágenes inspiracionales'}
                      </p>
                    </div>
                    <Button 
                      size="lg"
                      className="bg-white text-indigo-700 hover:bg-indigo-50 font-bold whitespace-nowrap"
                      onClick={handleGenerateMoodboard}
                      disabled={generatingMoodboard}
                    >
                      {generatingMoodboard ? (
                        <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> {isUSA ? 'Generating...' : 'Generando...'}</>
                      ) : (
                        <><Image className="w-5 h-5 mr-2" /> {isUSA ? 'Generate Moodboard' : 'Generar Moodboard'}</>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Moodboard Display */}
              {moodboard && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <Image className="w-5 h-5 text-purple-600" />
                        Moodboard - {getCategoryName(category)} {getSeasonName(season)} {year}
                      </span>
                      <Button variant="outline" size="sm" onClick={() => {
                        // Print moodboard
                        const printWindow = window.open('', '_blank');
                        printWindow.document.write(`
                          <html><head><title>Moodboard - TEXTREND AI</title>
                          <style>
                            body { font-family: Arial; padding: 20px; }
                            .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
                            img { width: 100%; border-radius: 8px; }
                            .palette { display: flex; height: 60px; margin-top: 20px; border-radius: 8px; overflow: hidden; }
                            .palette div { flex: 1; }
                          </style></head><body>
                          <h1>Moodboard - ${getCategoryName(category)} ${getSeasonName(season)} ${year}</h1>
                          <div class="grid">
                            ${moodboard.images?.map(img => `<img src="data:image/png;base64,${img.image_base64}" />`).join('')}
                          </div>
                          <div class="palette">
                            ${moodboard.color_palette?.map(c => `<div style="background:${c.hex}" title="${c.name}"></div>`).join('')}
                          </div>
                          </body></html>
                        `);
                        printWindow.document.close();
                        printWindow.print();
                      }}>
                        <Printer className="w-4 h-4 mr-1" />
                        {isUSA ? 'Print' : 'Imprimir'}
                      </Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {moodboard.images?.map((img, idx) => (
                        <div 
                          key={idx} 
                          className="relative group rounded-lg overflow-hidden cursor-pointer"
                          onClick={() => setImageModal({
                            src: `data:image/png;base64,${img.image_base64}`,
                            title: img.description || `Moodboard ${idx + 1}`,
                            description: ''
                          })}
                        >
                          <img 
                            src={`data:image/png;base64,${img.image_base64}`}
                            alt={img.description || `Moodboard ${idx + 1}`}
                            className="w-full aspect-square object-cover"
                          />
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                            <p className="text-white text-xs">{img.description}</p>
                          </div>
                          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Eye className="w-5 h-5 text-white" />
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Color Palette Visual */}
                    {moodboard.color_palette && (
                      <div className="mt-6">
                        <h4 className="font-semibold text-sm text-slate-700 mb-2">
                          {isUSA ? 'Season Color Palette' : 'Paleta de Colores de Temporada'}
                        </h4>
                        <div className="flex rounded-lg overflow-hidden h-16">
                          {moodboard.color_palette.map((color, idx) => (
                            <div 
                              key={idx} 
                              className="flex-1 relative group cursor-pointer"
                              style={{ backgroundColor: color.hex }}
                              title={color.name}
                            >
                              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-black/50 flex items-center justify-center transition-opacity">
                                <span className="text-white text-xs font-mono">{color.hex}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Methodology */}
              <Card className="border-slate-200 bg-slate-50/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-slate-600 text-base">
                    <BookOpen className="w-4 h-4" />
                    {isUSA ? 'Methodology' : 'Metodología'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2 text-sm text-slate-600">
                      <span className="text-indigo-500 mt-1">•</span>
                      <span>{isUSA ? 'Global fashion trends' : 'Tendencias globales de moda'}</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm text-slate-600">
                      <span className="text-indigo-500 mt-1">•</span>
                      <span>{isUSA ? 'International textile trade shows' : 'Ferias textiles internacionales'}</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm text-slate-600">
                      <span className="text-indigo-500 mt-1">•</span>
                      <span>{isUSA ? 'Runway analysis' : 'Análisis de pasarelas'}</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm text-slate-600">
                      <span className="text-indigo-500 mt-1">•</span>
                      <span>{isUSA ? 'Consumer behavior' : 'Comportamiento del consumidor'}</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm text-slate-600">
                      <span className="text-indigo-500 mt-1">•</span>
                      <span>{isUSA ? 'AI applied to fashion and textiles' : 'Inteligencia artificial aplicada a moda y textil'}</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm text-slate-600">
                      <span className="text-indigo-500 mt-1">•</span>
                      <span>{isUSA ? 'Public industry information' : 'Información pública de la industria'}</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* HISTORY TAB */}
        <TabsContent value="history" className="space-y-6">
          {loadingHistory ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
          ) : (
            <>
              {/* Past Analyses */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-indigo-600" />
                    {isUSA ? 'Past Analyses' : 'Análisis Anteriores'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {historyAnalyses.length === 0 ? (
                    <p className="text-slate-500 text-sm">{isUSA ? 'No analyses yet' : 'Sin análisis aún'}</p>
                  ) : (
                    <div className="space-y-2">
                      {historyAnalyses.map((a, idx) => (
                        <div 
                          key={idx}
                          className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer"
                          onClick={() => loadHistoryAnalysis(a)}
                        >
                          <div className="flex items-center gap-3">
                            <span>{CATEGORIES.find(c => c.id === a.category)?.icon}</span>
                            <div>
                              <p className="font-medium text-sm">
                                {getCategoryName(a.category)} - {getSeasonName(a.season)} {a.year}
                              </p>
                              <p className="text-xs text-slate-500">
                                {MARKETS.find(m => m.id === a.market)?.flag} {getMarketName(a.market)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-400">
                              {new Date(a.created_at).toLocaleDateString('es-MX')}
                            </span>
                            <ChevronRight className="w-4 h-4 text-slate-400" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Generated Patterns */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Image className="w-5 h-5 text-purple-600" />
                    {isUSA ? 'Generated Patterns' : 'Estampados Generados'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {historyPatterns.length === 0 ? (
                    <p className="text-slate-500 text-sm">{isUSA ? 'No patterns yet' : 'Sin estampados aún'}</p>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {historyPatterns.map((p, idx) => (
                        <div 
                          key={idx}
                          className="rounded-lg overflow-hidden border border-slate-200 cursor-pointer hover:shadow-md transition-shadow"
                          onClick={() => p.image_base64 && setImageModal({
                            src: `data:image/jpeg;base64,${p.image_base64}`,
                            title: p.pattern_name,
                            description: p.description,
                            patternData: p
                          })}
                        >
                          {p.image_base64 ? (
                            <img 
                              src={`data:image/jpeg;base64,${p.image_base64}`}
                              alt={p.pattern_name}
                              className="w-full aspect-square object-cover"
                            />
                          ) : (
                            <div className="w-full aspect-square bg-slate-100 flex items-center justify-center">
                              <Image className="w-8 h-8 text-slate-300" />
                            </div>
                          )}
                          <div className="p-2">
                            <p className="font-medium text-xs truncate">{p.pattern_name}</p>
                            <p className="text-xs text-slate-400">
                              {new Date(p.created_at).toLocaleDateString('es-MX')}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Generated Moodboards */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-amber-600" />
                    {isUSA ? 'Generated Moodboards' : 'Moodboards Generados'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {historyMoodboards.length === 0 ? (
                    <p className="text-slate-500 text-sm">{isUSA ? 'No moodboards yet' : 'Sin moodboards aún'}</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {historyMoodboards.map((m, idx) => (
                        <div key={idx} className="border border-slate-200 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-medium text-sm">
                              {getCategoryName(m.category)} - {getSeasonName(m.season)} {m.year}
                            </p>
                            <span className="text-xs text-slate-400">
                              {new Date(m.created_at).toLocaleDateString('es-MX')}
                            </span>
                          </div>
                          {m.images && (
                            <div className="grid grid-cols-3 gap-1">
                              {m.images.slice(0, 3).map((img, i) => (
                                <img 
                                  key={i}
                                  src={`data:image/png;base64,${img.image_base64}`}
                                  alt=""
                                  className="aspect-square object-cover rounded cursor-pointer"
                                  onClick={() => setImageModal({
                                    src: `data:image/png;base64,${img.image_base64}`,
                                    title: img.description || `Moodboard image`,
                                    description: ''
                                  })}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Image Modal */}
      {imageModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setImageModal(null)}>
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-bold text-lg">{imageModal.title}</h3>
              <button onClick={() => setImageModal(null)} className="p-1 hover:bg-slate-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              {imageModal.type === 'color' ? (
                <div className="text-center">
                  <div className="w-full h-48 rounded-lg mb-4" style={{ backgroundColor: imageModal.color.hex }} />
                  <h4 className="text-xl font-bold">{imageModal.color.name}</h4>
                  <div className="flex items-center justify-center gap-2 mt-1">
                    <p className="font-mono text-lg text-slate-600">{imageModal.color.hex}</p>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(imageModal.color.hex);
                        toast.success(isUSA ? 'HEX code copied!' : '¡Código HEX copiado!');
                      }}
                      className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded"
                      title={isUSA ? 'Copy HEX' : 'Copiar HEX'}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                  </div>
                  <a 
                    href="https://www.pantone.com/color-finder"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors text-sm font-medium"
                    onClick={() => {
                      navigator.clipboard.writeText(imageModal.color.hex);
                      toast.success(isUSA ? 'HEX copied! Paste it in the Pantone finder' : '¡HEX copiado! Pégalo en el buscador de Pantone');
                    }}
                  >
                    {isUSA ? 'Find Pantone equivalent' : 'Buscar equivalencia Pantone'}
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                  <p className="text-xs text-slate-500 mt-2">
                    {isUSA ? 'The HEX code will be copied automatically' : 'El código HEX se copiará automáticamente'}
                  </p>
                  <p className="text-slate-600 mt-4">{imageModal.color.description}</p>
                </div>
              ) : (
                <>
                  <img 
                    src={imageModal.src}
                    alt={imageModal.title}
                    className="w-full rounded-lg"
                  />
                  {imageModal.description && (
                    <p className="text-sm text-slate-600 mt-4">{imageModal.description}</p>
                  )}
                </>
              )}
              
              {/* Action buttons for patterns */}
              {imageModal.patternData && (
                <div className="mt-6 flex flex-wrap gap-3">
                  <Button
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => {
                      handleUseInDesigner(imageModal.patternData);
                      setImageModal(null);
                    }}
                  >
                    <Shirt className="w-4 h-4 mr-2" />
                    {isUSA ? 'Use in Fashion Designer' : 'Usar en Diseñador de Modas'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = imageModal.src;
                      link.download = `${imageModal.title.replace(/\s+/g, '_')}.png`;
                      link.click();
                    }}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {isUSA ? 'Download' : 'Descargar'}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Pattern Edit Modal */}
      {editingPattern && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setEditingPattern(null)}>
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-bold text-lg">
                {isUSA ? 'Edit Pattern' : 'Editar Estampado'}: {editingPattern.pattern_name}
              </h3>
              <button onClick={() => { setEditingPattern(null); setEditPrompt(''); }} className="p-1 hover:bg-slate-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <img 
                src={`data:image/jpeg;base64,${editingPattern.image_base64}`}
                alt={editingPattern.pattern_name}
                className="w-full rounded-lg mb-4"
              />
              
              <Label className="mb-2 block">
                {isUSA ? 'What changes do you want?' : '¿Qué cambios quieres hacer?'}
              </Label>
              <Textarea
                value={editPrompt}
                onChange={(e) => setEditPrompt(e.target.value)}
                placeholder={isUSA 
                  ? 'E.g.: Make the flowers larger, change background to cream color, add gold accents...'
                  : 'Ej: Hacer las flores más grandes, cambiar fondo a color crema, agregar acentos dorados...'}
                rows={3}
              />
              
              <div className="mt-4 flex gap-3">
                <Button
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                  onClick={() => handleEditPattern(editingPattern)}
                  disabled={editingPattern.loading || !editPrompt.trim()}
                >
                  {editingPattern.loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {isUSA ? 'Editing...' : 'Editando...'}</>
                  ) : (
                    <><Edit3 className="w-4 h-4 mr-2" /> {isUSA ? 'Apply Changes' : 'Aplicar Cambios'}</>
                  )}
                </Button>
              </div>

              {/* Quick Variation Buttons */}
              <div className="mt-6">
                <Label className="mb-2 block">{isUSA ? 'Quick Color Variations' : 'Variaciones Rápidas de Color'}</Label>
                <div className="flex flex-wrap gap-2">
                  {COLOR_VARIATIONS.map(v => (
                    <Button
                      key={v.id}
                      size="sm"
                      variant="outline"
                      onClick={() => handleGenerateVariation(editingPattern, v.id)}
                      disabled={generatingVariation === v.id}
                    >
                      {generatingVariation === v.id ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <span className="mr-1">{v.icon}</span>
                      )}
                      {isUSA ? v.nameEn : v.name}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TexTrendAI;
